import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFloppyDisk,
  faFolder,
  faArrowLeft,
  faFolderPlus,
  faHardDrive,
  faBuilding,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

/**
 * SaveToFile — a "Save as…" button + dialog. The user browses This PC → a drive
 * → folders and picks where to save, names the file, and saves. The text is
 * stored as a .txt file in that folder (uploaded to S3). Used by Notepad and
 * Sticky Notes. filesApi is lazy-imported so this doesn't pull axios into other
 * components' import graphs.
 */
const SaveAsDialog = ({ defaultName, content, onClose }) => {
  const me = useSelector((s) => s?.loginUserInfo?.loginUserDetails) || {};
  const drives = [
    { scope: "personal", name: [me.firstname, me.lastname].filter(Boolean).join(" ").trim() || "My Files", icon: faHardDrive },
    { scope: "organization", name: "Organization", icon: faBuilding },
    { scope: "department", name: me.department || "Department", icon: faUsers },
  ];

  const [path, setPath] = useState([{ scope: null, id: null, name: "This PC" }]);
  const current = path[path.length - 1];
  const scope = current.scope;

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filename, setFilename] = useState(defaultName || "Untitled");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadFolders = async (sc, parentId) => {
    if (!sc) return setFolders([]);
    setLoading(true);
    try {
      const { listFiles } = await import("./filesApi");
      const all = await listFiles(sc, parentId);
      setFolders(all.filter((n) => n.type === "folder"));
    } catch (e) {
      setError(e?.message || "Failed to load folders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFolders(scope, current.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, current.id]);

  const openDrive = (d) => setPath([path[0], { scope: d.scope, id: null, name: d.name }]);
  const openFolder = (f) => setPath((p) => [...p, { scope, id: f.id, name: f.name }]);
  const goToCrumb = (i) => setPath((p) => p.slice(0, i + 1));
  const back = () => path.length > 1 && goToCrumb(path.length - 2);

  const newFolder = async () => {
    setBusy(true);
    try {
      const { createFolder } = await import("./filesApi");
      const name = (window.prompt("New folder name", "New folder") || "").trim();
      if (name) {
        await createFolder(name, scope, current.id);
        await loadFolders(scope, current.id);
      }
    } catch (e) {
      setError(e?.message || "Failed to create folder");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!scope) return; // must be inside a drive
    setBusy(true);
    setError(null);
    try {
      const { shareTextToDrive } = await import("./filesApi");
      await shareTextToDrive(filename || "Untitled", content, scope, current.id);
      onClose(true);
    } catch (e) {
      setError(e?.message || "Failed to save");
      setBusy(false);
    }
  };

  return (
    <div className="webtop-personalize-backdrop" onMouseDown={() => onClose()}>
      <div className="webtop-saveas" role="dialog" aria-label="Save as" onMouseDown={(e) => e.stopPropagation()}>
        <div className="webtop-saveas__head">
          <button type="button" className="webtop-files__navbtn" onClick={back} disabled={path.length <= 1} aria-label="Back">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="webtop-files__crumbs">
            {path.map((c, i) => (
              <span key={`${c.scope || "root"}-${c.id || "root"}-${i}`} className="webtop-files__crumb">
                {i > 0 && <span className="webtop-files__crumbsep">›</span>}
                <button type="button" className="webtop-files__crumbbtn" onClick={() => goToCrumb(i)}>
                  {c.name}
                </button>
              </span>
            ))}
          </div>
          {scope && (
            <button type="button" className="webtop-files__actbtn" onClick={newFolder} disabled={busy}>
              <FontAwesomeIcon icon={faFolderPlus} /> New folder
            </button>
          )}
        </div>

        <div className="webtop-saveas__body">
          {!scope ? (
            <div className="webtop-saveas__list">
              {drives.map((d) => (
                <button key={d.scope} type="button" className="webtop-saveas__row" onClick={() => openDrive(d)}>
                  <FontAwesomeIcon icon={d.icon} className="webtop-saveas__rowicon" />
                  <span>{d.name}</span>
                </button>
              ))}
            </div>
          ) : loading ? (
            <div className="webtop-files__empty">Loading…</div>
          ) : folders.length === 0 ? (
            <div className="webtop-files__empty">No subfolders. Files save here.</div>
          ) : (
            <div className="webtop-saveas__list">
              {folders.map((f) => (
                <button key={f.id} type="button" className="webtop-saveas__row" onDoubleClick={() => openFolder(f)} onClick={() => openFolder(f)}>
                  <FontAwesomeIcon icon={faFolder} className="webtop-saveas__rowicon webtop-saveas__rowicon--folder" />
                  <span>{f.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div className="webtop-files__error">{error}</div>}

        <div className="webtop-saveas__foot">
          <input
            className="webtop-saveas__name"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder="File name"
            aria-label="File name"
          />
          <button type="button" className="webtop-saveas__cancel" onClick={() => onClose()}>
            Cancel
          </button>
          <button type="button" className="webtop-saveas__save" onClick={save} disabled={!scope || busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SaveToFile = ({ name, content, label = "Save as…" }) => {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const close = (ok) => {
    setOpen(false);
    if (ok === true) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };
  return (
    <span className="webtop-share">
      <button type="button" className="webtop-share__btn" onClick={() => setOpen(true)} title="Save as a file in a folder" aria-label="Save as">
        <FontAwesomeIcon icon={faFloppyDisk} />
        {label ? <span>{label}</span> : null}
      </button>
      {open && <SaveAsDialog defaultName={name} content={content} onClose={close} />}
      {saved && <span className="webtop-share__msg webtop-share__msg--ok">Saved ✓</span>}
    </span>
  );
};

export default SaveToFile;
